import {
  Activity,
  TrendingDown,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { DashboardKpis } from "../../../api/dashboard";
import { formatMinor } from "../../../lib/money";

interface KpiGridProps {
  kpis: DashboardKpis | null;
  isLoading: boolean;
}

interface KpiCardProps {
  title: string;
  value: string;
  hint: string;
  accentBarClass: string;
  iconClass: string;
  icon: ReactNode;
}

function KpiCard({
  title,
  value,
  hint,
  accentBarClass,
  iconClass,
  icon,
}: KpiCardProps) {
  return (
    <article className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${accentBarClass}`}
        aria-hidden="true"
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {title}
          </p>
          <p className="mt-1 text-xl font-bold tracking-tight text-godo-navy" dir="ltr">
            {value}
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{hint}</p>
        </div>

        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClass}`}
        >
          {icon}
        </div>
      </div>
    </article>
  );
}

export function KpiGrid({ kpis, isLoading }: KpiGridProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <section aria-label={t("dashboard.operational_kpis")}>
        <div className="mb-1 flex items-center gap-1.5">
          <Activity className="h-4 w-4 text-godo-navy" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900">
            {t("dashboard.operational_kpis")}
          </h2>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-20 animate-pulse rounded-lg border border-slate-200 bg-slate-100"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!kpis) {
    return (
      <section aria-label={t("dashboard.operational_kpis")}>
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-godo-navy" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-slate-900">
              {t("dashboard.operational_kpis")}
            </h2>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          {t("dashboard.kpis_unavailable")}
        </div>
      </section>
    );
  }

  return (
    <section aria-label={t("dashboard.operational_kpis")}>
      <div className="mb-1 flex items-center gap-1.5">
        <Activity className="h-4 w-4 text-godo-navy" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-slate-900">
          {t("dashboard.operational_kpis")}
        </h2>
        <span className="text-[10px] text-slate-400">{t("dashboard.period_filtered")}</span>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title={t("dashboard.kpi_total_sales")}
          value={formatMinor(kpis.totalSalesMinor)}
          hint={t("dashboard.kpi_total_sales_hint")}
          accentBarClass="bg-emerald-500"
          iconClass="bg-emerald-50 text-emerald-700"
          icon={<TrendingUp className="h-6 w-6" aria-hidden="true" />}
        />
        <KpiCard
          title={t("dashboard.kpi_total_expenses")}
          value={formatMinor(kpis.totalExpensesMinor)}
          hint={t("dashboard.kpi_total_expenses_hint")}
          accentBarClass="bg-red-500"
          iconClass="bg-red-50 text-red-700"
          icon={<TrendingDown className="h-6 w-6" aria-hidden="true" />}
        />
        <KpiCard
          title={t("dashboard.kpi_pending_work_orders")}
          value={String(kpis.pendingWorkOrdersCount)}
          hint={t("dashboard.kpi_pending_work_orders_hint")}
          accentBarClass="bg-amber-500"
          iconClass="bg-amber-50 text-amber-700"
          icon={<Wrench className="h-6 w-6" aria-hidden="true" />}
        />
        <KpiCard
          title={t("dashboard.kpi_active_employees")}
          value={String(kpis.activeEmployeesCount)}
          hint={t("dashboard.kpi_active_employees_hint")}
          accentBarClass="bg-sky-500"
          iconClass="bg-sky-50 text-sky-700"
          icon={<Users className="h-6 w-6" aria-hidden="true" />}
        />
      </div>
    </section>
  );
}
